/**
 * Enhanced FormattedResponse Component V2 - Bulletproof Markdown Rendering
 * 
 * This version implements a robust two-pass rendering strategy:
 * 1. First pass: Comprehensive text preprocessing to fix all list formatting issues
 * 2. Second pass: Add citations without breaking the formatted lists
 * 
 * Handles all edge cases for numbered lists including inline lists, various formats,
 * and ensures proper line breaks for correct Markdown parsing.
 */

import React, { memo, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkFootnotes from 'remark-footnotes';
import rehypeSanitize from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import rehypeExternalLinks from 'rehype-external-links';
import { defaultSchema } from 'hast-util-sanitize';
import type { FormattedResponseProps, Source } from '@/types';
import './FormattedResponse.css';

const FormattedResponseV2: React.FC<FormattedResponseProps> = memo(
  ({ text, sources = [], isDarkMode, colors, onCitationClick }) => {
    
    // Helper to get source URL
    const getSourceUrl = useCallback((source: Source): string | null => {
      if (!source) return null;
      if (source.excerpt_url) return source.excerpt_url;
      if (source.presigned_url) return source.presigned_url;
      if (source.uri && source.uri.startsWith('https://')) {
        let url = source.uri;
        if (source.page_number) url += `#page=${source.page_number}`;
        return url;
      }
      return null;
    }, []);

    // Render a single citation link
    const renderCitationLink = useCallback((num: number): React.ReactElement | null => {
      const sourceIndex = num - 1;
      const source = sources[sourceIndex];
      
      if (!source) return null;
      
      const hasDirectUrl = !!(source.excerpt_url || source.presigned_url || (source.uri && source.uri.startsWith('https://')));
      const urlToOpen = getSourceUrl(source);
      
      return (
        <sup
          key={`citation-${num}`}
          className="citation-wrapper"
          style={{
            marginLeft: '1px',
            marginRight: '1px',
            display: 'inline',
            fontSize: '0.875em',
            lineHeight: '0',
            verticalAlign: 'super'
          }}
        >
          <a
            href={urlToOpen || '#'}
            onClick={(e) => {
              if (!urlToOpen || (!e.ctrlKey && !e.metaKey)) {
                e.preventDefault();
                e.stopPropagation();
                onCitationClick?.(sourceIndex);
              }
            }}
            target={urlToOpen ? "_blank" : undefined}
            rel={urlToOpen ? "noopener noreferrer" : undefined}
            className="citation-link"
            style={{
              color: isDarkMode ? '#60a5fa' : '#2563eb',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'color 0.2s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
            title={`Citation ${num} - Click to view source${hasDirectUrl ? ' (Cmd/Ctrl-click to open URL)' : ''}`}
          >
            [{num}]
          </a>
        </sup>
      );
    }, [sources, isDarkMode, onCitationClick, getSourceUrl]);

    // Helper to render inline citations within text
    const renderTextWithInlineCitations = useCallback((
      text: string,
      renderCitation: (n: number) => React.ReactElement | null
    ): React.ReactNode => {
      // Find all citation patterns [n] not followed by (
      const citationRegex = /\[(\d+)\](?!\()/g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      while ((match = citationRegex.exec(text)) !== null) {
        const num = parseInt(match[1], 10);
        
        // Add text before citation
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }
        
        // Add citation if valid
        if (num > 0 && num <= sources.length) {
          const citation = renderCitation(num);
          if (citation) {
            parts.push(citation);
          }
        }
        
        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.length > 0 ? <>{parts}</> : text;
    }, [sources.length, renderCitationLink]);

    // Helper to process React nodes with inline citations
    const processNodesWithCitations = useCallback((
      nodes: React.ReactNode,
      renderCitation: (n: number) => React.ReactElement | null
    ): React.ReactNode => {
      return React.Children.map(nodes, (child) => {
        if (typeof child === 'string') {
          // Process text nodes to add inline citations
          return renderTextWithInlineCitations(child, renderCitation);
        }
        if (React.isValidElement(child)) {
          // Recursively process element children
          const childProps = child.props as any;
          if (childProps?.children) {
            const processedChildren = processNodesWithCitations(childProps.children, renderCitation);
            return React.cloneElement(child, { ...childProps, children: processedChildren });
          }
        }
        return child;
      });
    }, [renderTextWithInlineCitations]);

    /**
     * BULLETPROOF TEXT PREPROCESSOR
     * This is the core of the fix - a comprehensive two-pass text processor
     * that handles all edge cases for numbered lists
     */
    const preprocessText = useCallback((rawText: string): string => {
      if (!rawText || typeof rawText !== 'string') return '';
      
      // PASS 1: Basic normalization
      let text = rawText
        .replace(/\\n/g, '\n')
        .replace(/\\r\\n/g, '\n')
        .replace(/\\r/g, '\n')
        .replace(/\\t/g, '\t');
      
      // IMPORTANT: Protect citation markers and bold section headers from being modified
      // Store citations and bold headers temporarily and restore them after processing
      const citationPlaceholder = '___CITATION___';
      const citations: string[] = [];
      text = text.replace(/\[(\d+)\]/g, (match, num) => {
        citations.push(match);
        return `${citationPlaceholder}${citations.length - 1}___`;
      });
      
      // Fix section header formatting - ensure double line breaks before bold headers
      // This handles cases where citations appear right before section headers
      text = text.replace(/(\]|\.|!|\?)(\s*)(\*\*[^*]+\*\*)/g, '$1\n\n$3');
      
      // Ensure proper line breaks after bold section headers
      text = text.replace(/(\*\*[^*]+\*\*)\s*(?=\S)/g, '$1  \n');
      
      // PASS 2: Detect and fix ALL numbered list patterns
      
      // Pattern 1: Handle the broken ". 2 ." or " . 2 . " pattern
      // This is a critical fix for inline lists that appear as ". 2 . item text"
      text = text.replace(/\s*\.\s*(\d+)\s*\.\s*/g, (match, num) => {
        return '\n\n' + num + '. ';
      });
      
      // Pattern 2: Handle "1 . Item text 2 . Next item" (inline lists with spaces)
      // This catches lists that are all on one line
      text = text.replace(/(\d+)\s+\.\s+([^.]+?)(?=\s*\d+\s+\.|$)/g, (match, num, content) => {
        // Ensure each item starts on a new line
        return '\n\n' + num + '. ' + content.trim();
      });
      
      // Pattern 3: Fix "1. Item 2. Item" (no line breaks between items)
      // Look for number+period followed by text, then another number+period
      text = text.replace(/(\d+)\.\s*([^.\n]+?)(?=\s*\d+\.\s*)/g, (match, num, content) => {
        return num + '. ' + content.trim() + '\n\n';
      });
      
      // Pattern 4: Handle various numbered formats
      // "1 . " with space between number and period
      text = text.replace(/^(\s*)(\d+)\s+\.\s+/gm, '$1$2. ');
      text = text.replace(/\n(\s*)(\d+)\s+\.\s+/g, '\n$1$2. ');
      
      // "1) " parenthesis format
      text = text.replace(/^(\s*)(\d+)\)\s*/gm, '$1$2. ');
      text = text.replace(/\n(\s*)(\d+)\)\s*/g, '\n$1$2. ');
      
      // "1." without space after period
      text = text.replace(/^(\s*)(\d+)\.(\S)/gm, '$1$2. $3');
      text = text.replace(/\n(\s*)(\d+)\.(\S)/g, '\n$1$2. $3');
      
      // Pattern 5: Detect inline numbered lists that follow text
      // This is for cases like "Some text 1. First item 2. Second item"
      text = text.replace(/([a-zA-Z.!?;:,])\s*(\d+)\.\s+/g, (match, before, num) => {
        const numInt = parseInt(num);
        // Check if this is likely a list item (not a decimal number)
        if (numInt >= 1 && numInt <= 100) {
          // Check if the character before is end of sentence
          if (before.match(/[.!?]/)) {
            return before + '\n\n' + num + '. ';
          } else {
            // If mid-sentence, still break for list
            return before + '\n\n' + num + '. ';
          }
        }
        return match;
      });
      
      // Pattern 6: Ensure proper spacing between consecutive numbered items
      // This ensures that "1. Item\n2. Item" becomes "1. Item\n\n2. Item"
      text = text.replace(/^(\d+\.\s+.+)$/gm, (match) => {
        return match;
      });
      
      // Now ensure double line breaks between numbered items for proper Markdown parsing
      text = text.replace(/^(\d+\.\s+.+)\n(?=\d+\.\s+)/gm, '$1\n\n');
      
      // Pattern 7: Handle edge case where numbers appear after colons or semicolons
      text = text.replace(/([;:])\s*(\d+)\.\s+/g, (match, punct, num) => {
        const numInt = parseInt(num);
        if (numInt >= 1 && numInt <= 100) {
          return punct + '\n\n' + num + '. ';
        }
        return match;
      });
      
      // Pattern 8: Ensure lists at the beginning of text are properly formatted
      text = text.replace(/^\s*(\d+)\.\s+/m, '$1. ');
      
      // Pattern 9: Handle bullet points (convert various bullet symbols to standard dash)
      text = text.replace(/^(\s*)[-•●○▪▫◦‣⁃]\s+/gm, '$1- ');
      text = text.replace(/\n(\s*)[-•●○▪▫◦‣⁃]\s+/g, '\n$1- ');
      
      // Pattern 10: Ensure sub-items under numbered lists are properly indented
      // If a bullet follows a numbered item, indent it
      text = text.replace(/^(\d+\.\s+.+)\n(-\s+)/gm, '$1\n   $2');
      
      // PASS 3: Final cleanup
      
      // Remove excessive newlines (more than 2 consecutive)
      text = text.replace(/\n{4,}/g, '\n\n');
      text = text.replace(/\n{3}/g, '\n\n');
      
      // Ensure proper spacing before lists that follow paragraphs
      text = text.replace(/([^\n])\n(\d+\.\s+)/g, (match, before, listStart) => {
        // If the previous line doesn't end with a list item, add double newline
        if (!before.match(/^\d+\./)) {
          return before + '\n\n' + listStart;
        }
        return match;
      });
      
      // Ensure proper spacing before bullet lists that follow paragraphs
      text = text.replace(/([^\n])\n(-\s+)/g, (match, before, bulletStart) => {
        if (!before.match(/^[-\d]/)) {
          return before + '\n\n' + bulletStart;
        }
        return match;
      });
      
      // Restore the protected citations
      text = text.replace(new RegExp(`${citationPlaceholder}(\\d+)___`, 'g'), (match, index) => {
        return citations[parseInt(index)];
      });
      
      // Final trim
      return text.trim();
    }, []);

    // Apply the bulletproof preprocessor
    const normalizedText = useMemo(() => {
      return preprocessText(text);
    }, [text, preprocessText]);

    // Use the normalized text directly without artificial citation distribution
    const processedMarkdown = useMemo(() => {
      return normalizedText;
    }, [normalizedText]);

    // Custom components for ReactMarkdown
    const components = useMemo(() => ({
      // Handle paragraphs with inline citations
      p: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return <p {...props} className="mb-3 leading-7">{processedChildren}</p>;
      },
      
      // Handle list items with inline citations and proper nested list support
      li: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return (
          <li {...props} className="my-1 leading-7">
            {React.Children.map(processedChildren, (child, index) => {
              // Check if child is a nested list (ul or ol)
              if (React.isValidElement(child) && (child.type === 'ul' || child.type === 'ol')) {
                // Add proper spacing for nested lists
                return React.cloneElement(child as React.ReactElement<any>, {
                  className: `${(child.props as any).className || ''} mt-1 ml-4`
                });
              }
              return child;
            })}
          </li>
        );
      },
      
      // Style headings with inline citations
      h1: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return (
          <h1 {...props} className="text-2xl font-bold mt-4 mb-3" style={{ color: isDarkMode ? colors.darkBlue : colors.primaryBlue }}>
            {processedChildren}
          </h1>
        );
      },
      h2: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return (
          <h2 {...props} className="text-xl font-semibold mt-3 mb-2" style={{ color: isDarkMode ? colors.darkBlue : colors.primaryBlue }}>
            {processedChildren}
          </h2>
        );
      },
      h3: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return (
          <h3 {...props} className="text-lg font-semibold mt-3 mb-2" style={{ color: isDarkMode ? colors.darkBlue : colors.primaryBlue }}>
            {processedChildren}
          </h3>
        );
      },
      
      // Style lists with proper spacing and formatting
      ul: ({ children, ...props }: any) => (
        <ul {...props} className="list-disc pl-6 mb-3 space-y-1 marker:font-semibold">{children}</ul>
      ),
      ol: ({ children, ...props }: any) => (
        <ol {...props} className="list-decimal pl-6 mb-3 space-y-1 marker:font-semibold">{children}</ol>
      ),
      
      // Style code blocks
      code: ({ inline, className, children, ...props }: any) => {
        if (inline) {
          return (
            <code {...props} className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm">
              {children}
            </code>
          );
        }
        return (
          <code {...props} className={`block p-3 rounded bg-gray-100 dark:bg-gray-800 overflow-x-auto ${className || ''}`}>
            {children}
          </code>
        );
      },
      
      // Style blockquotes
      blockquote: ({ children, ...props }: any) => (
        <blockquote {...props} className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 my-3 italic">
          {children}
        </blockquote>
      ),
      
      // Style tables
      table: ({ children, ...props }: any) => (
        <div className="overflow-x-auto mb-4">
          <table {...props} className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            {children}
          </table>
        </div>
      ),
      thead: ({ children, ...props }: any) => (
        <thead {...props} className="bg-gray-50 dark:bg-gray-800">{children}</thead>
      ),
      tbody: ({ children, ...props }: any) => (
        <tbody {...props} className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {children}
        </tbody>
      ),
      
      // Handle links
      a: ({ href, children, ...props }: any) => (
        <a
          {...props}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {children}
        </a>
      ),
      
      // Handle table cells with inline citations
      th: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return (
          <th {...props} className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {processedChildren}
          </th>
        );
      },
      td: ({ children, ...props }: any) => {
        const processedChildren = processNodesWithCitations(children, renderCitationLink);
        return (
          <td {...props} className="px-4 py-2 whitespace-nowrap text-sm">
            {processedChildren}
          </td>
        );
      },
    }), [isDarkMode, colors, renderCitationLink, processNodesWithCitations]);

    // Sanitization schema - tight, no wildcard styles
    const sanitizeSchema = useMemo(() => ({
      ...defaultSchema,
      tagNames: [
        ...(defaultSchema.tagNames || []),
        'sup', 'sub', 'mark', 'ins', 'del', 'abbr', 'cite', 'time',
      ],
      attributes: {
        ...defaultSchema.attributes,
        a: ['href', 'target', 'rel', 'title', 'className'],
        p: ['className'],
        h1: ['className'],
        h2: ['className'],
        h3: ['className'],
        ul: ['className'],
        ol: ['className'],
        li: ['className'],
        code: ['className'],
        pre: ['className'],
        table: ['className'],
        thead: ['className'],
        tbody: ['className'],
        tr: ['className'],
        th: ['className'],
        td: ['className'],
        sup: ['className'],
      },
    }), []);

    // Handle empty text
    if (!text || (typeof text === 'string' && text.trim() === '')) {
      return null;
    }

    // Container classes for readability
    const containerClasses = `formatted-response prose prose-neutral ${
      isDarkMode ? 'prose-invert' : ''
    } max-w-3xl text-[15px] md:text-base leading-7 break-words`;

    return (
      <div className={containerClasses}>
        <ReactMarkdown
          remarkPlugins={[
            remarkGfm,
            [remarkFootnotes as any, { inlineNotes: true }]
          ]}
          rehypePlugins={[
            [rehypeSanitize, sanitizeSchema],
            rehypeSlug,
            [rehypeExternalLinks, { target: '_blank', rel: ['noopener', 'noreferrer'] }],
          ]}
          components={components}
        >
          {processedMarkdown}
        </ReactMarkdown>
      </div>
    );
  }
);

FormattedResponseV2.displayName = 'FormattedResponseV2';

export default FormattedResponseV2;